// src/components/inputs/MultiImageUploader/UploadingImageCard.jsx

import React from "react";
import { Box, IconButton, Typography, LinearProgress, Tooltip } from "@mui/material";
import {
    CloudUpload,
    Close,
    CheckCircle,
    Error as ErrorIcon,
    Refresh,
} from "@mui/icons-material";

const UploadingImageCard = ({
    item,
    tempId,
    status,
    error,
    isArabic,
    isDark,
    onRemove,
    onRetry,
}) => {
    // Show preview if horizontal item or status exists
    const previewUrl = item?.preview;

    if (!status) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: 180,
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                }}
            >
                {previewUrl ? (
                    <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }} />
                ) : (
                    <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 1, opacity: 0.5 }} />
                )}

                <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    onClick={onRemove}
                >
                    <Close fontSize="small" />
                </IconButton>

                <Typography variant="body2" color="white" sx={{ position: 'absolute', bottom: 8, bgcolor: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1 }}>
                    {isArabic ? 'في الانتظار...' : 'Waiting...'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: 180,
                border: '2px dashed',
                borderColor: status.status === 'error' ? 'error.main' : 'primary.main',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                p: 2,
            }}
        >
            <IconButton
                size="small"
                sx={{ position: 'absolute', top: 4, right: 4 }}
                onClick={onRemove}
                disabled={status.status === 'uploading' || status.status === 'compressing'}
            >
                <Close fontSize="small" />
            </IconButton>

            {/* Status icon or Preview */}
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                {previewUrl ? (
                    <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.3 }} />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CloudUpload sx={{ fontSize: 48, color: 'grey.400', opacity: 0.2 }} />
                    </Box>
                )}
            </Box>

            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {status.status === 'compressing' && (
                    <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                )}
                {status.status === 'uploading' && (
                    <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                )}
                {status.status === 'success' && (
                    <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                )}
                {status.status === 'error' && (
                    <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                )}

                {/* Status text */}
                <Typography variant="body2" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
                    {status.status === 'compressing' && (isArabic ? 'جاري الضغط...' : 'Compressing...')}
                    {status.status === 'uploading' && (isArabic ? 'جاري الرفع...' : 'Uploading...')}
                    {status.status === 'success' && (isArabic ? 'تم الرفع بنجاح' : 'Uploaded successfully')}
                    {status.status === 'error' && (error || (isArabic ? 'فشل الرفع' : 'Upload failed'))}
                </Typography>
            </Box>

            {/* Progress bar */}
            {(status.status === 'compressing' || status.status === 'uploading') && (
                <LinearProgress
                    variant="determinate"
                    value={status.progress}
                    sx={{ width: '80%', mb: 1 }}
                />
            )}

            {/* Retry button */}
            {status.status === 'error' && (
                <Tooltip title={isArabic ? 'إعادة المحاولة' : 'Retry'}>
                    <IconButton
                        color="primary"
                        onClick={onRetry}
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default UploadingImageCard;
